import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  CircleDot,
  LayoutGrid,
  Sparkles,
  Wand2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";
import { TableCard } from "@/components/TableCard";
import { ValidationPanel } from "@/components/ValidationPanel";
import { ToDecideTray } from "@/components/ToDecideTray";
import { GuestInspector } from "@/components/GuestInspector";
import { GuestListPanel } from "@/components/GuestListPanel";
import { MoveGuestDialog } from "@/components/MoveGuestDialog";
import { RoundTable } from "@/components/RoundTable";
import {
  avoidMapOf,
  conflictedGuestIds,
  domainLabel,
  guestMapOf,
  wishMapOf,
} from "@/lib/format";
import { Button } from "@/components/ui/button";
import { t, tf } from "@/i18n";

export const PlanBoard = ({
  plan,
  onMove,
  onToggleLock,
  onTogglePin,
  onRename,
  onSuggest,
  headTable,
  childrenTable,
}) => {
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [draggingGuest, setDraggingGuest] = useState(null);
  const [moveGuest, setMoveGuest] = useState(null);
  const [view, setView] = useState("cards");
  const [zoom, setZoom] = useState(1);
  const [refining, setRefining] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [moves, setMoves] = useState(null);
  const [asking, setAsking] = useState(false);

  const pick = (guest) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(guest.id)) next.delete(guest.id);
      else next.add(guest.id);
      return next;
    });

  const ask = async () => {
    setAsking(true);
    try {
      const { moves: found } = await onSuggest([...picked]);
      setMoves(found);
      if (!found.length) {
        toast.info(t("plan.alreadyBest"), { description: t("plan.alreadyBestDesc") });
      }
    } catch (error) {
      toast.error(t("plan.cannotCompute"));
    } finally {
      setAsking(false);
    }
  };

  const applyMove = async (move) => {
    for (const guestId of move.guest_ids) {
      await onMove(guestId, move.to_table, false);
    }
    setMoves((prev) => prev.filter((m) => m !== move));
    setPicked((prev) => {
      const next = new Set(prev);
      move.guest_ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const guestMap = useMemo(() => guestMapOf(plan), [plan]);
  const avoidMap = useMemo(() => avoidMapOf(plan), [plan]);
  const wishMap = useMemo(() => wishMapOf(plan), [plan]);
  const conflictIds = useMemo(() => conflictedGuestIds(plan), [plan]);
  const validationByTable = useMemo(() => {
    const map = {};
    (plan?.validation?.tables || []).forEach((table) => {
      map[table.table] = table;
    });
    return map;
  }, [plan]);

  const selected = selectedGuest ? guestMap[selectedGuest.id] || selectedGuest : null;

  const blockedNamesFor = (tableNumber) => {
    if (!draggingGuest) return [];
    const avoids = avoidMap[draggingGuest.id] || [];
    const table = (plan?.tables || []).find((item) => item.table === tableNumber);
    if (!table) return [];
    return avoids
      .filter((id) => table.guest_ids.includes(id))
      .map((id) => guestMap[id]?.name || id);
  };

  const requestMove = (guestId, targetTable, moveParty = false) => {
    const guest = guestMap[guestId];
    if (!guest) return;
    setMoveGuest(null);
    if (guest.table === targetTable) return;
    const avoids = avoidMap[guestId] || [];
    const table = (plan?.tables || []).find((item) => item.table === targetTable);
    const clash = table
      ? avoids.filter((id) => table.guest_ids.includes(id)).map((id) => guestMap[id]?.name || id)
      : [];
    if (clash.length) {
      toast.error(tf("plan.askedNotWith", { name: guest.name, names: clash.join(", ") }), {
        description: t("plan.neverBreak"),
        action: {
          label: t("plan.placeAnyway"),
          onClick: () => onMove(guestId, targetTable, moveParty),
        },
        duration: 9000,
      });
      return;
    }
    onMove(guestId, targetTable, moveParty);
  };

  if (!plan) return null;

  return (
    <div className="space-y-5" data-testid="plan-board">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[290px_minmax(0,1fr)] 2xl:grid-cols-[290px_minmax(0,1fr)_340px]">
        <div className="order-2 lg:order-1">
          <GuestListPanel
            plan={plan}
            conflictIds={conflictIds}
            selectedGuestId={selected?.id}
            draggingGuest={draggingGuest}
            onSelectGuest={setSelectedGuest}
            onRequestMove={setMoveGuest}
            onDragStart={setDraggingGuest}
            onDragEnd={() => setDraggingGuest(null)}
          />
        </div>

        <div className="order-1 min-w-0 lg:order-2">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div
              className="inline-flex rounded-lg p-0.5"
              style={{ backgroundColor: "var(--paper-2)" }}
              data-testid="plan-view-toggle"
            >
              {[
                { id: "cards", label: t("plan.cards"), icon: LayoutGrid },
                { id: "round", label: t("plan.round"), icon: CircleDot },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setView(option.id)}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors"
                  style={{
                    backgroundColor: view === option.id ? "#ffffff" : "transparent",
                    color: view === option.id ? "var(--ink)" : "var(--muted-ink)",
                    boxShadow: view === option.id ? "0 1px 3px rgba(31,35,32,0.08)" : "none",
                  }}
                  data-testid={`plan-view-${option.id}`}
                >
                  <option.icon className="h-3.5 w-3.5" />
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={refining ? "default" : "outline"}
                size="sm"
                className="h-8"
                onClick={() => {
                  setRefining((on) => !on);
                  setPicked(new Set());
                  setMoves(null);
                }}
                data-testid="refine-toggle"
              >
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                {refining ? t("plan.finish") : t("plan.refine")}
              </Button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(0.7, Math.round((z - 0.15) * 100) / 100))}
                disabled={zoom <= 0.7}
                className="rounded-md p-1.5 disabled:opacity-40"
                style={{ backgroundColor: "var(--paper-2)" }}
                aria-label={t("plan.zoomOut")}
                data-testid="plan-zoom-out"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </button>
              <span className="font-num w-11 text-center text-[11.5px] text-[color:var(--muted-ink)]">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(1.9, Math.round((z + 0.15) * 100) / 100))}
                disabled={zoom >= 1.9}
                className="rounded-md p-1.5 disabled:opacity-40"
                style={{ backgroundColor: "var(--paper-2)" }}
                aria-label={t("plan.zoomIn")}
                data-testid="plan-zoom-in"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {refining ? (
            <div
              className="mb-3 rounded-xl p-3"
              style={{ backgroundColor: "var(--paper-2)", border: "1px solid var(--gild)" }}
              data-testid="refine-panel"
            >
              <p className="text-[13px] font-semibold">{t("plan.refineTitle")}</p>
              <p className="mt-0.5 text-[12px] text-[color:var(--ink-soft)]">{t("plan.refineHint")}</p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-num text-[12px] font-semibold">{tf("plan.picked", { n: picked.size })}</span>
                <Button
                  size="sm"
                  className="h-8"
                  disabled={!picked.size || asking}
                  onClick={ask}
                  data-testid="refine-ask"
                >
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  {asking ? t("plan.computing") : t("plan.propose")}
                </Button>
                {picked.size ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      setPicked(new Set());
                      setMoves(null);
                    }}
                  >
                    {t("plan.clear")}
                  </Button>
                ) : null}
              </div>

              {moves && moves.length ? (
                <ul className="mt-3 space-y-1.5" data-testid="refine-moves">
                  {moves.map((move, index) => (
                    <li
                      key={index}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
                    >
                      <span className="min-w-0 text-[12.5px]">
                        <span className="font-semibold">{move.names.join(" + ")}</span>
                        <span className="mx-2 inline-flex items-center gap-1 text-[color:var(--muted-ink)]">
                          {t("plan.table")} {move.from_table} <ArrowRight className="h-3 w-3" /> {move.to_table}
                        </span>
                        {move.kind === "swap" && move.swap_with && move.swap_with.length ? (
                          <span className="text-[11.5px] text-[color:var(--muted-ink)]">
                            {tf("plan.swapWith", { names: move.swap_with.join(", ") })}
                          </span>
                        ) : null}
                        {move.shared && move.shared.length ? (
                          <span className="block text-[11.5px] text-[color:var(--sage-dark)]">
                            {tf("plan.shares", { things: move.shared.map(domainLabel).join(", ") })}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <Button
                          size="sm"
                          className="h-7 text-[11.5px]"
                          onClick={() => applyMove(move)}
                          data-testid={"refine-accept-" + index}
                        >
                          {t("plan.move")}
                        </Button>
                        <button
                          type="button"
                          onClick={() => setMoves((prev) => prev.filter((m) => m !== move))}
                          className="rounded-full p-1 text-[color:var(--muted-ink)] hover:bg-[color:var(--paper-2)]"
                          aria-label={t("plan.dismiss")}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {view === "round" ? (
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(300 * zoom)}px, 1fr))`,
              }}
              data-testid="round-table-grid"
            >
              {(plan.tables || []).map((table) => (
                <div
                  key={table.table}
                  className="surface rounded-xl p-3"
                  style={{
                    borderColor: validationByTable[table.table]?.conflicts?.length
                      ? "var(--danger)"
                      : undefined,
                  }}
                >
                  <RoundTable
                    table={table}
                    guests={(table.guest_ids || []).map((id) => guestMap[id]).filter(Boolean)}
                    conflictIds={conflictIds}
                    selectedGuestId={selected?.id}
                    onSelectGuest={setSelectedGuest}
                    size={Math.round(400 * zoom)}
                  />
                  <p className="clamp-2 px-2 pb-1 text-center text-[11.5px] italic text-[color:var(--ink-soft)]">
                    {table.justification}
                  </p>
                </div>
              ))}
            </div>
          ) : (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(340 * zoom)}px, 1fr))`,
            }}
          >
            {(plan.tables || []).map((table) => (
              <TableCard
                key={table.table}
                table={table}
                validation={validationByTable[table.table]}
                guests={(table.guest_ids || []).map((id) => guestMap[id]).filter(Boolean)}
                conflictIds={conflictIds}
                selectedGuestId={selected?.id}
                draggingGuest={draggingGuest}
                blockedBy={blockedNamesFor(table.table)}
                onDropGuest={(guestId, tableNumber) => requestMove(guestId, tableNumber)}
                onSelectGuest={refining ? pick : setSelectedGuest}
                pickedIds={refining ? picked : null}
                onRequestMove={setMoveGuest}
                onToggleLock={onToggleLock}
                onRename={onRename}
                isHeadTable={headTable === table.table}
                isChildrenTable={childrenTable === table.table}
                onDragStart={setDraggingGuest}
                onDragEnd={() => setDraggingGuest(null)}
              />
            ))}
          </div>
          )}
        </div>

        <div className="order-3 space-y-5">
          <ValidationPanel
            plan={plan}
            guestMap={guestMap}
            onFocusGuest={(guest) => setSelectedGuest(guest)}
          />
          <GuestInspector
            guest={selected}
            plan={plan}
            guestMap={guestMap}
            wishMap={wishMap}
            avoidMap={avoidMap}
            onTogglePin={onTogglePin}
            onClose={() => setSelectedGuest(null)}
          />
        </div>
      </div>

      <ToDecideTray
        plan={plan}
        guestMap={guestMap}
        onSelectGuest={setSelectedGuest}
        onRequestMove={setMoveGuest}
        onDropToTray={(guestId) => onMove(guestId, null)}
        draggingGuest={draggingGuest}
        onDragStart={setDraggingGuest}
        onDragEnd={() => setDraggingGuest(null)}
      />

      <MoveGuestDialog
        guest={moveGuest ? guestMap[moveGuest.id] || moveGuest : null}
        plan={plan}
        avoidMap={avoidMap}
        guestMap={guestMap}
        onMove={requestMove}
        onClose={() => setMoveGuest(null)}
      />
    </div>
  );
};

export default PlanBoard;
